import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import {
  Box,
  Typography,
  MenuItem,
  TextField,
  Autocomplete,
  InputAdornment,
  IconButton,
  Tooltip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import React from "react";
import useCountries from "../../../../hooks/useCountries";
import {
  exitLevelOptions,
  isBetweenRange,
  isValidNumberInput,
} from "../../../../helpers/financialIndicesHelper";
import EditIcon from "@mui/icons-material/Edit";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import useQueryState from "../../../../hooks/useQueryState";
import { Feature } from "../../../../types/geojson";
import PayoutChart from "./partials/PayoutChart";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import RemoveIcon from "@mui/icons-material/Remove";
import CompressIcon from "@mui/icons-material/Compress";
import { MONTHS } from "../../../../Components/Select/SelectCriticalWindow/enums";
import { ANOMALY_LAYERS } from "../../../../enums/layers";
import CompareGeoDialog from "./partials/CompareGeoDialog";
import { useUIStore } from "../../../../store/ui";

const FinancialIndices = () => {
  const [open, setOpen] = useState(false);
  const {
    queryState: {
      aggregationLevel,
      country,
      season,
      layers,
      payout: {
        geographicalAreas,
        sumInsured,
        deductible,
        minPayout,
        maxPayout,
        seasons,
      },
    },
    queryState,
    setQueryState,
  } = useQueryState();
  const {
    getCountryAdministrativeUnit: {
      data: geoUnit,
      isLoading: isLoadingGeographicalUnit,
    },
  } = useCountries({ country, aggregationLevel });
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (season.length === 1) {
      setQueryState({
        ...queryState,
        payout: {
          ...queryState.payout,
          seasons: { 1: 0, 2: 0, [season[0]]: 100 },
        },
      });
    } else if (season.length === 0) {
      setQueryState({
        ...queryState,
        payout: { ...queryState.payout, seasons: { 1: 0, 2: 0 } },
      });
    }
  }, [season]);

  const clearEditLayer = useUIStore((s) => s.clearEditLayer);

  useEffect(() => {
    return () => {
      setQueryState((prev) => ({
        ...prev,
        payout: {
          ...prev.payout,
          comparisonFocus: 0,
        },
        ui: {
          ...prev.ui,
        },
      }));
      clearEditLayer();
    };
  }, []);

  const handleToggleComparison = (checked: boolean) => {
    setQueryState((prev) => {
      if (checked) {
        const layerOne = prev.layers.find((l) => l.season === "1" && l.active);
        const layerTwo = prev.layers.find((l) => l.season === "2" && l.active);

        const nextLayers = prev.layers.map((l) => {
          const isSeason1Active =
            !!layerOne &&
            l.displayName === layerOne.displayName &&
            l.season === "1";

          const isSeason2Active =
            !!layerTwo &&
            l.displayName === layerTwo.displayName &&
            l.season === "2";

          return {
            ...l,
            active: isSeason1Active || isSeason2Active,
          };
        });

        return {
          ...prev,
          layers: nextLayers,
          payout: {
            ...prev.payout,
            comparisonFocus: 1,
          },
        };
      }

      return {
        payout: {
          ...prev.payout,
          comparisonFocus: 0,
          geographicalAreas: prev.payout.geographicalAreas?.[0]
            ? [prev.payout.geographicalAreas[0]]
            : [],
        },
      };
    });
  };

  const handleGeographicalAreasChange = (
    _event: SyntheticEvent<Element, Event>,
    values: Feature[],
  ) => {
    if (values.length > 0)
      setQueryState({
        ...queryState,
        payout: {
          ...queryState.payout,
          geographicalAreas: values.map(
            (value) => value?.properties?.location_id ?? "",
          ),
        },
      });
  };

  const handleExitLevelChange = (event: ChangeEvent<{ value: unknown }>) => {
    const selectedValue = event.target.value as number;
    setQueryState((prev) => ({
      ...prev,
      payout: {
        ...prev.payout,
        exitLevel: selectedValue,
      },
    }));
  };

  const handleTriggerLevelChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    const selectedValue = event.target.value as number;
    setQueryState((prev) => ({
      ...prev,
      payout: {
        ...prev.payout,
        triggerLevel: selectedValue,
      },
    }));
  };

  const handleSwitchSeasonDistribution = () => {
    setQueryState((prev) => ({
      ...prev,
      payout: {
        ...prev.payout,
        seasons: { 1: prev.payout.seasons["2"], 2: prev.payout.seasons["1"] },
      },
    }));
  };

  const handleChangeTab = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue === 1 && !localStorage.getItem("compareGeoDialog"))
      setOpen(true);
    handleToggleComparison(newValue === 1);
    setTab(newValue);
  };

  const toggleEditLayer = useUIStore((s) => s.toggleEditLayer);

  const handleEditLayer = (value: string) => {
    toggleEditLayer(value);
  };

  return (
    <Box width="100%">
      <Box
        sx={{
          maxHeight: "100%",
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "1px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#002244",
          },
        }}
      >
        <Box
          sx={{
            margin: "25px 50px",
          }}
        >
          <Box marginBottom={3}>
            <Typography variant="h1" marginBottom="12px">
              Simulate historical payout
            </Typography>
            <Typography variant="body1" color="#515167">
              Select a comparison focus and define the payout parameters.
              Results will be visualized in a downloadable chart showing
              historical payouts.
            </Typography>
          </Box>
          <Stack>
            <Tabs
              value={tab}
              onChange={handleChangeTab}
              variant="fullWidth"
              sx={{
                width: "100%",
                borderBottom: "1px solid #E8E8ED",
                "& .MuiTab-root": {
                  textTransform: "none",
                  color: "#555",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: "#2146A4",
                },
                "& .MuiTabs-indicator": {
                  borderBottom: "2px solid #2146A4",
                },
              }}
            >
              <Tab
                value={0}
                label={
                  <Stack direction="row" alignItems="center" gap={1}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="23"
                      height="20"
                      viewBox="0 0 23 20"
                      fill="none"
                    >
                      <path
                        d="M17.5682 4.89621C17.5682 4.89621 15.9583 8.27149 15.4322 11.6119C14.9258 14.8274 15.2494 19.9317 15.2494 19.9317H17.2594C17.2594 19.9317 16.3047 14.8724 16.4372 11.6119C16.5711 8.31859 17.5682 4.89621 17.5682 4.89621Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M3.92998 8.6124C3.92998 8.6124 6.76827 10.845 7.29435 13.9112C7.80075 16.8627 7.63317 19.9317 7.63317 19.9317H5.62324C5.62324 19.9317 6.42191 16.904 6.28938 13.9112C6.15552 10.8882 3.92998 8.6124 3.92998 8.6124Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M5.48182 16.6459C5.28507 16.8195 5.06741 16.8708 4.98618 16.888C4.78955 16.9296 4.58221 16.9243 4.44324 16.9163C4.14046 16.8988 3.77662 16.8385 3.52104 16.7648C3.2678 16.6918 3.06891 16.535 2.9735 16.4568C2.85191 16.357 2.73207 16.2406 2.62861 16.1359C2.36917 15.8735 2.26094 15.7426 2.08812 15.5747L0.507444 14.0398L2.71043 14.012C2.89827 14.0097 3.18945 14.006 3.4674 14.0175C3.60621 14.0233 3.75609 14.0334 3.8972 14.0518C4.02021 14.0678 4.21049 14.0985 4.38921 14.1747C4.57314 14.2531 4.74728 14.3816 4.86011 14.4702C4.99238 14.574 5.12986 14.6962 5.25268 14.818C5.36849 14.9328 5.50343 15.0785 5.60577 15.2298C5.65072 15.2962 5.73461 15.4283 5.7822 15.5986C5.80581 15.6831 5.84686 15.9016 5.78032 16.1415C5.77757 16.1534 5.77758 16.1663 5.77431 16.1782C5.71584 16.3919 5.59509 16.5459 5.48182 16.6459ZM3.77329 15.8906C4.1418 15.9968 4.9574 16.0923 4.89425 15.9061C5.02351 15.8049 4.29924 15.1264 4.03165 15.0124C3.79124 14.9104 3.10202 14.9176 2.72201 14.9224C3.0426 15.2339 3.52765 15.8198 3.77329 15.8906Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M5.0246 12.9265C4.81462 13.0839 4.59357 13.1177 4.51122 13.1283C4.3119 13.1541 4.10564 13.1323 3.96775 13.1132C3.66733 13.0716 3.30947 12.9825 3.06058 12.8886C2.81397 12.7956 2.62822 12.6235 2.53937 12.5379C2.42612 12.4288 2.31595 12.3031 2.22117 12.1905C1.9835 11.9082 1.88606 11.7692 1.72718 11.588L0.274026 9.93185L2.47221 10.0799C2.65963 10.0926 2.95018 10.1122 3.22632 10.1458C3.36423 10.1627 3.51282 10.1847 3.65202 10.2143C3.77335 10.2401 3.96058 10.2859 4.13265 10.376C4.30974 10.4689 4.47307 10.6109 4.57847 10.7082C4.70203 10.8222 4.82932 10.955 4.94204 11.0862C5.04831 11.2099 5.1712 11.3659 5.26115 11.5248C5.30065 11.5946 5.37374 11.733 5.40758 11.9065C5.42437 11.9927 5.44786 12.2137 5.36239 12.4476C5.3587 12.4592 5.35768 12.4721 5.35347 12.4837C5.27814 12.692 5.14548 12.8359 5.0246 12.9265ZM3.38177 12.0374C3.74063 12.1726 4.54602 12.3329 4.49793 12.1422C4.63484 12.0517 3.96702 11.3176 3.70939 11.1826C3.47789 11.0617 2.79028 11.0139 2.41111 10.9883C2.70583 11.3244 3.14257 11.9472 3.38177 12.0374Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M15.1996 7.15702C14.9441 7.21675 14.7281 7.15886 14.6484 7.13553C14.4556 7.07903 14.2754 6.97623 14.1568 6.90334C13.8984 6.74452 13.6066 6.5191 13.4163 6.33318C13.2279 6.14891 13.1269 5.91669 13.08 5.80259C13.0201 5.65715 12.9697 5.4978 12.9282 5.35663C12.824 5.00262 12.7906 4.83613 12.7179 4.60639L12.0527 2.50592L14.0061 3.52476C14.1726 3.61167 14.4308 3.74633 14.6702 3.88812C14.7897 3.95893 14.9169 4.03881 15.0325 4.12185C15.1332 4.19424 15.2863 4.31138 15.4076 4.46309C15.5325 4.61923 15.625 4.81491 15.6824 4.94636C15.7498 5.10047 15.813 5.27322 15.8635 5.43863C15.9111 5.59459 15.9609 5.78683 15.9794 5.96849C15.9876 6.04829 15.9989 6.20434 15.9602 6.37687C15.9409 6.46252 15.8736 6.67437 15.7014 6.85417C15.6934 6.86331 15.6872 6.87469 15.6787 6.88368C15.526 7.04416 15.3467 7.12264 15.1996 7.15702ZM14.0525 5.6827C14.3268 5.95076 14.9999 6.4211 15.0325 6.22722C15.1942 6.19933 14.8777 5.25871 14.696 5.03156C14.5326 4.82784 13.9221 4.50786 13.5852 4.33205C13.72 4.75822 13.8697 5.50401 14.0525 5.6827Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M17.9111 11.6416C18.1666 11.7013 18.3826 11.6434 18.4623 11.6201C18.6552 11.5636 18.8353 11.4608 18.9539 11.3879C19.2123 11.2291 19.5042 11.0037 19.6944 10.8177C19.8828 10.6335 19.9838 10.4012 20.0308 10.2871C20.0906 10.1417 20.141 9.98236 20.1826 9.84119C20.2868 9.48718 20.3201 9.32068 20.3928 9.09095L21.0581 6.99048L19.1046 8.00932C18.9381 8.09622 18.6799 8.23089 18.4406 8.37267C18.321 8.44348 18.1938 8.52337 18.0782 8.60641C17.9775 8.6788 17.8245 8.79594 17.7031 8.94765C17.5782 9.10379 17.4857 9.29947 17.4283 9.43092C17.361 9.58503 17.2978 9.75778 17.2473 9.92318C17.1997 10.0791 17.1498 10.2714 17.1313 10.4531C17.1232 10.5329 17.1118 10.6889 17.1506 10.8614C17.1698 10.9471 17.2371 11.1589 17.4094 11.3387C17.4174 11.3479 17.4235 11.3592 17.432 11.3682C17.5847 11.5287 17.764 11.6072 17.9111 11.6416ZM19.0582 10.1673C18.784 10.4353 18.1108 10.9057 18.0783 10.7118C17.9165 10.6839 18.2331 9.74327 18.4148 9.51612C18.5782 9.3124 19.1887 8.99242 19.5256 8.8166C19.3907 9.24277 19.241 9.98857 19.0582 10.1673Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M17.9285 4.6511C18.184 4.71083 18.4 4.65294 18.4797 4.62961C18.6726 4.57311 18.8527 4.47031 18.9713 4.39742C19.2297 4.2386 19.5216 4.01318 19.7118 3.82726C19.9002 3.64299 20.0012 3.41077 20.0482 3.29667C20.108 3.15123 20.1584 2.99188 20.2 2.85071C20.3042 2.4967 20.3375 2.33021 20.4102 2.10047L21.0755 0L19.122 1.01884C18.9555 1.10574 18.6973 1.24041 18.458 1.38219C18.3384 1.453 18.2112 1.53289 18.0956 1.61593C17.9949 1.68832 17.8418 1.80546 17.7205 1.95717C17.5956 2.11331 17.5031 2.30899 17.4457 2.44044C17.3784 2.59455 17.3152 2.7673 17.2647 2.93271C17.2171 3.08867 17.1672 3.28091 17.1487 3.46257C17.1406 3.54237 17.1292 3.69842 17.168 3.87095C17.1872 3.9566 17.2545 4.16845 17.4267 4.34825C17.4348 4.35739 17.4409 4.36877 17.4494 4.37776C17.6021 4.53824 17.7814 4.61672 17.9285 4.6511ZM19.0756 3.17678C18.8014 3.44484 18.1282 3.91518 18.0957 3.7213C17.9339 3.69341 18.2505 2.75279 18.4321 2.52564C18.5956 2.32192 19.2061 2.00194 19.543 1.82612C19.4081 2.25229 19.2584 2.99809 19.0756 3.17678Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M17.6185 16.3215C17.8429 16.4575 18.0662 16.4695 18.1492 16.472C18.3501 16.4781 18.5532 16.4363 18.6886 16.4038C18.9835 16.333 19.3309 16.2092 19.5694 16.0915C19.8057 15.9748 19.9737 15.7853 20.0538 15.6914C20.1558 15.5717 20.2531 15.4359 20.3364 15.3145C20.5453 15.0103 20.6287 14.8624 20.769 14.6665L22.0531 12.8761L19.88 13.2387C19.6947 13.2696 19.4074 13.3175 19.1359 13.3781C19.0003 13.4083 18.8546 13.4448 18.719 13.4879C18.6007 13.5254 18.4189 13.5893 18.2565 13.6959C18.0893 13.8056 17.9407 13.9629 17.8453 14.0701C17.7335 14.1957 17.6198 14.3403 17.5205 14.4819C17.4269 14.6154 17.3198 14.7827 17.2459 14.9497C17.2134 15.023 17.1542 15.1678 17.1375 15.3439C17.1292 15.4313 17.1275 15.6535 17.2355 15.8779C17.2403 15.8891 17.2425 15.9018 17.2479 15.913C17.3432 16.1129 17.4893 16.2431 17.6185 16.3215ZM19.1664 15.2757C18.8225 15.4455 18.0367 15.6838 18.0659 15.4894C17.9208 15.4127 18.5135 14.6167 18.7567 14.4571C18.9752 14.3141 19.6549 14.1993 20.0297 14.1367C19.7693 14.5 19.3956 15.1626 19.1664 15.2757Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M14.5396 11.3012C14.3058 11.4203 14.0822 11.4157 13.9992 11.4121C13.7984 11.4033 13.599 11.3465 13.4664 11.3041C13.1775 11.2116 12.8402 11.0624 12.6111 10.9273C12.3841 10.7934 12.2306 10.5921 12.1577 10.4925C12.0648 10.3656 11.9778 10.2229 11.9037 10.0957C11.718 9.77686 11.6458 9.62316 11.5203 9.41745L10.3725 7.53677L12.5127 8.05935C12.6952 8.10395 12.9781 8.17302 13.2444 8.25351C13.3774 8.29371 13.52 8.34088 13.6521 8.3939C13.7672 8.44013 13.9438 8.5173 14.0979 8.63562C14.2565 8.7574 14.393 8.92531 14.4802 9.03923C14.5824 9.1728 14.685 9.32544 14.7736 9.47399C14.8571 9.61405 14.9514 9.78882 15.0128 9.96079C15.0398 10.0363 15.0881 10.1852 15.0917 10.3619C15.0935 10.4497 15.0787 10.6715 14.9545 10.8873C14.9488 10.898 14.9456 10.9106 14.9395 10.9213C14.8296 11.1136 14.6742 11.2327 14.5396 11.3012ZM13.0734 10.1437C13.4038 10.3385 14.1698 10.6343 14.1551 10.4383C14.3055 10.3726 13.7734 9.53482 13.5427 9.35767C13.3353 9.19889 12.6661 9.03405 12.2969 8.94383C12.5297 9.32544 12.8532 10.0139 13.0734 10.1437Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M14.1431 16.2663C13.9092 16.3853 13.6857 16.3807 13.6027 16.3771C13.4019 16.3683 13.2024 16.3116 13.0699 16.2691C12.781 16.1766 12.4437 16.0275 12.2146 15.8924C11.9876 15.7585 11.8341 15.5571 11.7612 15.4575C11.6683 15.3306 11.5813 15.1879 11.5072 15.0608C11.3215 14.7419 11.2493 14.5882 11.1238 14.3825L9.97594 12.5018L12.1162 13.0244C12.2987 13.069 12.5816 13.1381 12.8479 13.2186C12.9809 13.2588 13.1235 13.3059 13.2556 13.359C13.3707 13.4052 13.5473 13.4824 13.7014 13.6007C13.8599 13.7225 13.9965 13.8904 14.0837 14.0043C14.1859 14.1379 14.2885 14.2905 14.3771 14.439C14.4606 14.5791 14.5549 14.7539 14.6163 14.9259C14.6433 15.0014 14.6916 15.1502 14.6952 15.327C14.697 15.4148 14.6822 15.6366 14.5579 15.8523C14.5523 15.8631 14.5491 15.8756 14.543 15.8864C14.433 16.0787 14.2777 16.1978 14.1431 16.2663ZM12.6769 15.1088C13.0073 15.3035 13.7733 15.5994 13.7586 15.4033C13.909 15.3376 13.3769 14.4999 13.1462 14.3227C12.9388 14.164 12.2695 13.9991 11.9004 13.9089C12.1331 14.2905 12.4567 14.9789 12.6769 15.1088Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M7.90948 11.8133C8.17073 11.7888 8.35716 11.6653 8.42531 11.6179C8.59026 11.5031 8.72842 11.3484 8.81772 11.2416C9.01229 11.009 9.21747 10.7025 9.3388 10.4658C9.45897 10.2312 9.48094 9.97897 9.48924 9.85585C9.49981 9.69894 9.49698 9.53184 9.49155 9.38478C9.47794 9.016 9.45669 8.84753 9.45266 8.60661L9.41638 6.4036L7.88765 7.99008C7.75734 8.12538 7.55527 8.33507 7.37335 8.54553C7.2825 8.65063 7.18724 8.76679 7.10402 8.88223C7.0315 8.98287 6.92356 9.14255 6.85668 9.32494C6.78786 9.51266 6.76229 9.72758 6.74959 9.87047C6.7347 10.038 6.72964 10.2219 6.73427 10.3947C6.73864 10.5578 6.75243 10.7559 6.79257 10.934C6.81021 11.0123 6.84901 11.1638 6.94054 11.3151C6.98599 11.3902 7.1171 11.5697 7.33751 11.6855C7.34804 11.6916 7.35744 11.7005 7.3684 11.7063C7.56414 11.81 7.75909 11.8275 7.90948 11.8133ZM8.52898 10.051C8.35404 10.3923 7.86513 11.0521 7.77269 10.8786C7.61046 10.9035 7.61189 9.91106 7.71202 9.63797C7.80228 9.3929 8.27954 8.8956 8.54319 8.62189C8.55067 9.06882 8.64558 9.82355 8.52898 10.051Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M9.79049 13.6303C9.58179 13.7037 9.39709 13.7814 9.30611 13.8559C9.08156 14.0404 8.5777 14.8935 8.72896 14.9555C8.7207 15.1519 9.47641 14.8309 9.80025 14.6254C9.89711 14.5639 10.0103 14.3891 10.1264 14.1801L10.6538 15.0443C10.5726 15.151 10.4515 15.2905 10.2885 15.394C10.064 15.5365 9.7314 15.6963 9.44576 15.7983C9.31473 15.845 9.1176 15.9088 8.91744 15.9242C8.83465 15.9306 8.61112 15.9418 8.37349 15.8305C8.23672 15.7664 8.07754 15.6528 7.96138 15.4643C7.9549 15.4537 7.95075 15.4417 7.94478 15.4311C7.81359 15.2197 7.79163 14.9985 7.79049 14.9106C7.78828 14.7341 7.83143 14.5838 7.85592 14.5072C7.91161 14.3333 8.00069 14.1549 8.07955 14.0121C8.16315 13.8608 8.26019 13.7045 8.35787 13.5678C8.44124 13.4511 8.57257 13.2787 8.72701 13.1518C8.877 13.0286 9.05102 12.9459 9.16451 12.8959C9.21085 12.8755 9.25893 12.8562 9.30709 12.8373L9.79049 13.6303Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M12.2065 12.3461L11.3178 12.1293L12.6284 11.6264L12.2065 12.3461Z"
                        fill="#2146A4"
                      />
                      <path
                        d="M3.4013 8.60941C3.14952 8.68328 2.93063 8.63753 2.84977 8.61867C2.65405 8.57301 2.46846 8.48042 2.34599 8.41425C2.07915 8.27008 1.77516 8.06127 1.57487 7.88625C1.37645 7.71276 1.26271 7.48653 1.20946 7.37522C1.14159 7.23335 1.0824 7.07706 1.03304 6.93842C0.909256 6.59077 0.866675 6.42639 0.781286 6.20107L0 4.14094L2.00718 5.04932C2.1783 5.12681 2.44361 5.24687 2.69048 5.37509C2.81377 5.43913 2.94524 5.5118 3.06527 5.58827C3.16987 5.65494 3.32923 5.76336 3.45884 5.90807C3.59222 6.05701 3.69549 6.24723 3.76015 6.37527C3.83594 6.52539 3.90868 6.69435 3.96833 6.85668C4.02457 7.00975 4.08505 7.19891 4.11366 7.37926C4.12622 7.45849 4.14625 7.61366 4.11718 7.78808C4.10274 7.87467 4.04734 8.08995 3.88541 8.27906C3.87789 8.28863 3.87243 8.30033 3.86439 8.30979C3.7209 8.47853 3.54625 8.56688 3.4013 8.60941ZM2.1738 7.20131C2.46258 7.45367 3.16088 7.88577 3.18258 7.69037C3.34253 7.6535 2.97404 6.73199 2.77998 6.51532C2.60546 6.32103 1.97809 6.03557 1.63189 5.87881C1.79028 6.2968 1.98131 7.0331 2.1738 7.20131Z"
                        fill="#2146A4"
                      />
                    </svg>
                    <Stack direction="column" justifyContent="flex-start">
                      <Typography textAlign="left">
                        Compare drought indices
                      </Typography>
                    </Stack>
                  </Stack>
                }
                sx={{ textTransform: "none" }}
              />
              <Tab
                value={1}
                label={
                  <Stack direction="row" alignItems="center" gap={1}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="25"
                      viewBox="0 0 24 25"
                      fill="none"
                    >
                      <path
                        d="M21.9 18.2L21.3 11.7C22.3 11.2 23 10.2 23 9C23 7.3 21.7 6 20 6C19.3 6 18.6 6.3 18.1 6.7L12.9 3.5C13 3.4 13 3.2 13 3C13 1.3 11.7 0 10 0C8.3 0 7 1.3 7 3C7 3.9 7.4 4.7 8.1 5.3L3.1 17C3.1 17 3.1 17 3 17C1.3 17 0 18.3 0 20C0 21.7 1.3 23 3 23C4.3 23 5.4 22.2 5.8 21.1L18.2 22C18.6 23.2 19.7 24.1 21 24.1C22.7 24.1 24 22.8 24 21.1C24 19.7 23.1 18.5 21.9 18.2ZM20 8C20.6 8 21 8.4 21 9C21 9.6 20.6 10 20 10C19.4 10 19 9.6 19 9C19 8.4 19.4 8 20 8ZM10 2C10.6 2 11 2.4 11 3C11 3.6 10.6 4 10 4C9.4 4 9 3.6 9 3C9 2.4 9.4 2 10 2ZM9.9 6C10 6 10 6 9.9 6C10.7 6 11.4 5.7 11.9 5.3L17 8.4C17 8.6 17 8.8 17 9C17 10.4 18 11.6 19.3 11.9L19.9 18.2C19.1 18.5 18.5 19.1 18.2 19.9L5.8 19.1C5.6 18.6 5.3 18.1 4.9 17.8L9.9 6ZM3 21C2.4 21 2 20.6 2 20C2 19.4 2.4 19 3 19C3.6 19 4 19.4 4 20C4 20.6 3.6 21 3 21ZM21 22C20.4 22 20 21.6 20 21C20 20.4 20.4 20 21 20C21.6 20 22 20.4 22 21C22 21.6 21.6 22 21 22Z"
                        fill="#515167"
                      />
                      <path d="M13 8H11V10H13V8Z" fill="#515167" />
                      <path d="M13 12H11V18H13V12Z" fill="#515167" />
                    </svg>
                    <Stack direction="column" justifyContent="flex-start">
                      <Typography textAlign="left">
                        Compare geographical units
                      </Typography>
                    </Stack>
                  </Stack>
                }
                sx={{ textTransform: "none" }}
              />
            </Tabs>
          </Stack>
          <Stack mt={1}>
            {tab === 0 && (
              <Stack gap={1}>
                <Typography variant="body1" color="#515167">
                  Find a preselection of enabled indices from previous steps and
                  compare it to a specific geographical unit in the selected
                  target area
                </Typography>
                <Stack gap={2} mt={2}>
                  {season.length > 0 &&
                    season.map((each) => (
                      <Stack key={`season-${each}`}>
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          color="#1B1B22"
                        >
                          Enabled season {each} indices
                        </Typography>
                        <Stack gap={0.5}>
                          {layers
                            .filter(
                              (layer) => layer.season === each && layer.active,
                            )
                            .sort((a: any, b: any) => {
                              const A = a.displayName ?? "";
                              const B = b.displayName ?? "";

                              const isHybridA = A === "Hybrid Index";
                              const isHybridB = B === "Hybrid Index";

                              if (isHybridA && !isHybridB) return 1;
                              if (!isHybridA && isHybridB) return -1;

                              return A.localeCompare(B);
                            })
                            .map((layer) => (
                              <Stack
                                direction="row"
                                padding={1}
                                justifyContent="space-between"
                                sx={{
                                  borderRadius: "8px",
                                  border: "1px solid #E3F2FD",
                                  background: "#FFF",
                                }}
                              >
                                <Stack
                                  direction="row"
                                  gap={1}
                                  alignItems="center"
                                >
                                  <Stack>
                                    {
                                      ANOMALY_LAYERS.find(
                                        (each) =>
                                          layer.displayName ===
                                          each.displayName,
                                      )?.icon
                                    }
                                  </Stack>
                                  <Typography variant="body2" color="#000">
                                    {layer.displayName}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    gap={0.5}
                                    alignItems="center"
                                  >
                                    <Typography variant="body2" color="#515167">
                                      {MONTHS[
                                        Number(layer.months[0]) - 1
                                      ].slice(0, 3)}
                                      , Dekad {layer.dekads[0]}
                                    </Typography>
                                    <ArrowRightAltIcon
                                      fontSize="small"
                                      sx={{ color: "#515167" }}
                                    />
                                    <Typography variant="body2" color="#515167">
                                      {MONTHS[
                                        Number(layer.months[1]) - 1
                                      ].slice(0, 3)}
                                      , Dekad {layer.dekads[1]}
                                    </Typography>
                                  </Stack>
                                </Stack>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleEditLayer(
                                      `${layer.displayName}-${each}`,
                                    )
                                  }
                                >
                                  <EditIcon
                                    fontSize="small"
                                    sx={{ color: "#2146A4" }}
                                  />
                                </IconButton>
                              </Stack>
                            ))}
                        </Stack>
                      </Stack>
                    ))}
                </Stack>
              </Stack>
            )}
          </Stack>
          <Stack marginBottom={5}>
            {tab === 1 && (
              <Stack gap={1}>
                <Typography variant="body1" color="#515167">
                  Select multiple geographical units to compare across{" "}
                  <b>one </b>
                  drought index (anomalies)
                </Typography>
                <Stack>
                  <Autocomplete
                    disableCloseOnSelect
                    sx={{
                      ".MuiOutlinedInput-root": { borderColor: "red" },
                      // width: "50%",
                    }}
                    fullWidth
                    multiple
                    onChange={handleGeographicalAreasChange}
                    value={
                      geoUnit?.features
                        ? geoUnit.features.filter((unit) =>
                            geographicalAreas.includes(
                              unit.properties?.location_id ?? "",
                            ),
                          )
                        : []
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.properties?.location_id ===
                      value?.properties?.location_id
                    }
                    getOptionLabel={(option) =>
                      option.properties?.geounit ?? ""
                    }
                    getOptionDisabled={(option) =>
                      Boolean(
                        // queryState.payout.geographicalAreas.length >= 3 ||
                        queryState.payout.geographicalAreas.find(
                          (area) => area === option.properties?.location_id,
                        ),
                      )
                    }
                    options={geoUnit?.features ?? []}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Geographical Unit"
                        placeholder="Geographical Unit"
                      />
                    )}
                  />
                </Stack>
                <Stack gap={2} mt={2}>
                  {season.length > 0 &&
                    season.map((each) => (
                      <Stack>
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          color="#1B1B22"
                        >
                          Enabled season {each} index
                        </Typography>
                        <Stack gap={0.5}>
                          {layers
                            .filter(
                              (layer) => layer.season === each && layer.active,
                            )
                            .sort((a, b) =>
                              a.displayName.localeCompare(b.displayName),
                            )
                            .map((layer) => (
                              <Stack
                                direction="row"
                                padding={1}
                                justifyContent="space-between"
                                sx={{
                                  borderRadius: "8px",
                                  border: "1px solid #E3F2FD",
                                  background: "#FFF",
                                }}
                              >
                                <Stack
                                  direction="row"
                                  gap={1}
                                  alignItems="center"
                                >
                                  <Stack>
                                    {
                                      ANOMALY_LAYERS.find(
                                        (each) =>
                                          layer.displayName ===
                                          each.displayName,
                                      )?.icon
                                    }
                                  </Stack>
                                  <Typography variant="body2" color="#000">
                                    {layer.displayName}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    gap={0.5}
                                    alignItems="center"
                                  >
                                    <Typography variant="body2" color="#515167">
                                      {MONTHS[
                                        Number(layer.months[0]) - 1
                                      ].slice(0, 3)}
                                      , Dekad {layer.dekads[0]}
                                    </Typography>
                                    <ArrowRightAltIcon
                                      fontSize="small"
                                      sx={{ color: "#515167" }}
                                    />
                                    <Typography variant="body2" color="#515167">
                                      {MONTHS[
                                        Number(layer.months[1]) - 1
                                      ].slice(0, 3)}
                                      , Dekad {layer.dekads[1]}
                                    </Typography>
                                  </Stack>
                                </Stack>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleEditLayer(
                                      `${layer.displayName}-${each}`,
                                    )
                                  }
                                >
                                  <EditIcon
                                    fontSize="small"
                                    sx={{ color: "#2146A4" }}
                                  />
                                </IconButton>
                              </Stack>
                            ))}
                        </Stack>
                      </Stack>
                    ))}
                </Stack>
              </Stack>
            )}
          </Stack>
          <Stack marginBottom="48px">
            <Accordion
              expanded={expanded}
              onChange={(_, isExpanded) => setExpanded(isExpanded)}
            >
              <AccordionSummary
                expandIcon={
                  <IconButton sx={{ borderRadius: "8px" }}>
                    <Typography color="#2146A4" fontWeight={600}>
                      {expanded ? "Hide Inputs" : "Show Inputs"}
                    </Typography>
                    <ExpandMoreIcon
                      sx={{
                        color: "#2146A4",
                        transform: expanded ? `rotate(180deg)` : undefined,
                      }}
                    />
                  </IconButton>
                }
                sx={{
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    transform: "none !important",
                  },
                  "&.Mui-expanded": {
                    // display: "none",
                    borderTop: "none",
                  },
                }}
              >
                <Typography marginBottom="12px" variant="h5" fontWeight={550}>
                  Define payout parameters
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ borderBottom: "none !important" }}>
                <Stack gap={2}>
                  <Typography variant="subtitle1">
                    You can simulate the historical payout analysis based on a
                    number of parameters you define yourself.
                  </Typography>
                  <Stack direction="row" gap={2} alignItems="center">
                    <TextField
                      sx={{ flex: "1" }}
                      select
                      label={
                        <Tooltip title="Frequency of the event that will trigger the payout.​">
                          <Typography>Trigger Level</Typography>
                        </Tooltip>
                      }
                      value={queryState.payout.triggerLevel}
                      onChange={(event: ChangeEvent<{ value: unknown }>) =>
                        handleTriggerLevelChange(event)
                      }
                    >
                      {exitLevelOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={
                            Number(option.value) <=
                            Number(queryState.payout.exitLevel)
                          }
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ArrowRightAltIcon />
                    <TextField
                      sx={{ flex: "1" }}
                      label={
                        <Tooltip title="Frequency of the event for which maximum payout is reached.​​">
                          <Typography>Exit Level</Typography>
                        </Tooltip>
                      }
                      select
                      value={queryState.payout.exitLevel}
                      onChange={(event: ChangeEvent<{ value: unknown }>) =>
                        handleExitLevelChange(event)
                      }
                    >
                      {exitLevelOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={
                            Number(option.value) >=
                            Number(queryState.payout.triggerLevel)
                          }
                        >
                          {option.label}{" "}
                          {Boolean(
                            option.value > queryState.payout.triggerLevel,
                          )}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  <Stack direction="row" gap={2} alignItems="center">
                    <TextField
                      label={
                        <Tooltip title="Total value insured in USD.​​​">
                          <Typography>Sum Insured</Typography>
                        </Tooltip>
                      }
                      fullWidth
                      value={sumInsured}
                      onChange={(e) => {
                        if (isValidNumberInput(e.target.value)) {
                          setQueryState((prev) => ({
                            ...prev,
                            payout: {
                              ...prev.payout,
                              sumInsured: e.target.value,
                            },
                          }));
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography fontSize="24px">$</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <RemoveIcon />
                    <TextField
                      label={
                        <Tooltip title="Percentage of the Sum insured that will be deducted from the payout.​​​">
                          <Typography>Deductible</Typography>
                        </Tooltip>
                      }
                      fullWidth
                      value={deductible}
                      onChange={(e) => {
                        if (
                          isValidNumberInput(e.target.value) &&
                          isBetweenRange(Number(e.target.value), 0, 100)
                        ) {
                          setQueryState({
                            ...queryState,
                            payout: {
                              ...queryState.payout,
                              deductible: e.target.value,
                            },
                          });
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography fontSize="24px">%</Typography>
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{
                        min: 0,
                        max: 100,
                        maxLength: 5,
                      }}
                    />
                  </Stack>
                  <Stack direction="row" gap={2} alignItems="center">
                    <TextField
                      label={
                        <Tooltip title="Percentage of the Sum insured that needs to be reached to trigger a payout.​​​​">
                          <Typography>Minimum Payout</Typography>
                        </Tooltip>
                      }
                      fullWidth
                      value={minPayout}
                      onChange={(e) => {
                        if (
                          isValidNumberInput(e.target.value) &&
                          isBetweenRange(Number(e.target.value), 0, 100)
                        ) {
                          setQueryState({
                            ...queryState,
                            payout: {
                              ...queryState.payout,
                              minPayout: e.target.value,
                            },
                          });
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography fontSize="24px">%</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <CompressIcon />
                    <TextField
                      fullWidth
                      label={
                        <Tooltip title="Percentage of the Sum insured that cannot be exceeded for the payout.​​">
                          <Typography>Maximum Payout</Typography>
                        </Tooltip>
                      }
                      value={maxPayout}
                      onChange={(e) => {
                        if (
                          isValidNumberInput(e.target.value) &&
                          isBetweenRange(Number(e.target.value), 1, 100)
                        ) {
                          setQueryState({
                            ...queryState,
                            payout: {
                              ...queryState.payout,
                              maxPayout: e.target.value,
                            },
                          });
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography fontSize="24px">%</Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                  <Stack gap={2}>
                    <Typography variant="subtitle1">
                      Define payout distribution for seasons. Must equate to
                      100% in total.
                    </Typography>
                    <Stack direction="row" gap={1} alignItems="center">
                      <TextField
                        label={<Typography>Season 1 Payout</Typography>}
                        value={seasons["1"]}
                        fullWidth
                        onChange={(e) => {
                          if (
                            isValidNumberInput(e.target.value) &&
                            isBetweenRange(Number(e.target.value), 0, 100)
                          ) {
                            setQueryState((prev) => ({
                              ...prev,
                              payout: {
                                ...queryState.payout,
                                seasons: {
                                  ...queryState.payout.seasons,
                                  1: Number(e.target.value),
                                },
                              },
                            }));
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography fontSize="24px">%</Typography>
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{
                          min: 0,
                          max: 100,
                          maxLength: 5,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root.Mui-disabled": {
                            backgroundColor: "#f2f2f27d",
                          },
                          "& .MuiOutlinedInput-root.Mui-disabled fieldset": {
                            backgroundColor: "#f2f2f27d",
                            borderColor: "#c4c4c4",
                          },
                          "& .MuiInputBase-input.Mui-disabled": {
                            color: "#9e9e9e",
                            // WebkitTextFillColor: '#ffffffff',
                          },
                          "& .MuiInputLabel-root.Mui-disabled": {
                            color: "#9e9e9e",
                          },
                          "& .MuiSvgIcon-root.Mui-disabled": {
                            color: "#9e9e9e",
                          },
                        }}
                        disabled={season.length < 2}
                      />
                      <IconButton
                        disabled={season.length < 2}
                        onClick={handleSwitchSeasonDistribution}
                      >
                        <CompareArrowsIcon />
                      </IconButton>
                      <TextField
                        label={<Typography>Season 2 Payout</Typography>}
                        value={seasons["2"]}
                        fullWidth
                        onChange={(e) => {
                          if (
                            isValidNumberInput(e.target.value) &&
                            isBetweenRange(Number(e.target.value), 0, 100)
                          ) {
                            setQueryState({
                              ...queryState,
                              payout: {
                                ...queryState.payout,
                                seasons: {
                                  ...queryState.payout.seasons,
                                  2: Number(e.target.value),
                                },
                              },
                            });
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography fontSize="24px">%</Typography>
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{
                          min: 0,
                          max: 100,
                          maxLength: 5,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root.Mui-disabled": {
                            backgroundColor: "#f2f2f27d",
                          },
                          "& .MuiOutlinedInput-root.Mui-disabled fieldset": {
                            backgroundColor: "#f2f2f27d",
                            borderColor: "#c4c4c4",
                          },
                          "& .MuiInputBase-input.Mui-disabled": {
                            color: "#9e9e9e",
                            // WebkitTextFillColor: '#ffffffff',
                          },
                          "& .MuiInputLabel-root.Mui-disabled": {
                            color: "#9e9e9e",
                          },
                          "& .MuiSvgIcon-root.Mui-disabled": {
                            color: "#9e9e9e",
                          },
                        }}
                        disabled={season.length < 2}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
            {!expanded && (
              <Stack gap={2}>
                <Divider />
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  <Chip
                    label={
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "#15252EDE" }}
                      >
                        <b>Trigger Level: </b>
                        {
                          exitLevelOptions.find(
                            (each) =>
                              each.value === queryState.payout.triggerLevel,
                          )?.label
                        }
                      </Typography>
                    }
                  />
                  <Chip
                    label={
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "#15252EDE" }}
                      >
                        <b>Exit Level: </b>
                        {
                          exitLevelOptions.find(
                            (each) =>
                              each.value === queryState.payout.exitLevel,
                          )?.label
                        }
                      </Typography>
                    }
                  />
                  <Chip
                    label={
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "#15252EDE" }}
                      >
                        <b>Sum Insured: </b>${queryState.payout.sumInsured}
                      </Typography>
                    }
                  />
                  <Chip
                    label={
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "#15252EDE" }}
                      >
                        <b>Deductible: </b>
                        {queryState.payout.deductible}%
                      </Typography>
                    }
                  />
                  <Chip
                    label={
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "#15252EDE" }}
                      >
                        <b>Minimum Payout: </b>
                        {queryState.payout.minPayout}%
                      </Typography>
                    }
                  />
                  <Chip
                    label={
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "#15252EDE" }}
                      >
                        <b>Maximum Payout: </b>
                        {queryState.payout.maxPayout}%
                      </Typography>
                    }
                  />
                  <Chip
                    sx={{
                      background: "#E3F2FD",
                    }}
                    label={
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "#15252EDE" }}
                      >
                        <b>Season 1 Payout: </b>
                        {queryState.payout.seasons[1]}%
                      </Typography>
                    }
                  />
                  <Chip
                    sx={{
                      background: "#E3F2FD",
                    }}
                    label={
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "#15252EDE" }}
                      >
                        <b>Season 2 Payout: </b>
                        {queryState.payout.seasons[2]}%
                      </Typography>
                    }
                  />
                </Stack>
              </Stack>
            )}
          </Stack>
          <Box height={350}>
            <Stack sx={{ border: "1px solid #ccc", height: "100%" }}>
              <PayoutChart />
            </Stack>
          </Box>
        </Box>
      </Box>
      <CompareGeoDialog open={open} setOpen={setOpen} />
    </Box>
  );
};

export default FinancialIndices;
